import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Topheader } from '../../components/Topheader';
import { renderAnnouncementMessage } from '../../components/Announcement/Announcement';
import { DataGetApi } from '../../helpers/PostDataApi';
import config from '../../constants/config';
import './AnnouncementGroups.css';

const getToken = () => JSON.parse(localStorage.getItem('user') || '{}')?.token;

const formatAppliedDate = (date) => (date ? new Date(date).toLocaleString() : 'Unknown');

const AnnouncementHistory = () => {
  const { locationId } = useParams();
  const location = useLocation();
  const groupPath = location.state?.groupId ? `/Announcement/groups/${location.state.groupId}` : '/Announcement';
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewAnnouncement, setPreviewAnnouncement] = useState(null);

  useEffect(() => {
    let isMounted = true;
    DataGetApi(`${config.BASEURL}announcement/locations/${locationId}/history`, {}, getToken()).then((response) => {
      if (!isMounted) return;
      if (response?.status === 200 && Array.isArray(response.data?.history)) {
        setHistory(response.data.history);
      } else {
        setError('Unable to load announcement history.');
      }
      setIsLoading(false);
    });
    return () => {
      isMounted = false;
    };
  }, [locationId]);

  return (
    <div className="rightPanel">
      <Topheader />
      <main className="contentPart announcementHistoryPage">
        <Button component={Link} startIcon={<ArrowBackIcon />} to={groupPath}>
          {location.state?.groupId ? 'Back to group' : 'All groups'}
        </Button>
        <div className="announcementDetailHeading">
          <div>
            <Typography component="h1" variant="h5">
              Announcement history
            </Typography>
            <Typography color="textSecondary" variant="body2">
              MID: {location.state?.mId || 'Not set'}
            </Typography>
          </div>
        </div>
        {isLoading && <CircularProgress aria-label="Loading announcement history" />}
        {error && (
          <Typography color="error" role="alert">
            {error}
          </Typography>
        )}
        {!isLoading && !error && (
          <TableContainer component={Paper} className="announcementHistoryTable">
            <Table aria-label="Location announcement history">
              <TableHead>
                <TableRow>
                  <TableCell>Announcement</TableCell>
                  <TableCell>Applied</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item, index) => {
                  const announcement = item.announcement;
                  const isDisabled = !announcement || announcement.enabled === false;
                  return (
                    <TableRow
                      className={item.current ? 'announcementHistoryCurrent' : ''}
                      key={item._id || `${item.announcementId}-${index}`}
                    >
                      <TableCell>
                        {isDisabled ? (
                          'Announcement disabled'
                        ) : (
                          <Button
                            aria-label={`Preview ${announcement.title || 'Untitled announcement'}`}
                            onClick={() => setPreviewAnnouncement(announcement)}
                            size="small"
                          >
                            {announcement.title || 'Untitled announcement'}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>{formatAppliedDate(item.assignedAt)}</TableCell>
                      <TableCell>
                        {item.current ? (
                          <span>
                            <CheckCircleIcon className="announcementActiveIcon" /> Current
                          </span>
                        ) : isDisabled ? (
                          'Disabled'
                        ) : (
                          'Previous'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!history.length && (
                  <TableRow>
                    <TableCell colSpan={3}>No announcement history found.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Dialog
          open={Boolean(previewAnnouncement)}
          onClose={() => setPreviewAnnouncement(null)}
          aria-labelledby="announcement-history-preview-title"
        >
          <DialogTitle id="announcement-history-preview-title">
            {previewAnnouncement?.title || 'Announcement preview'}
          </DialogTitle>
          <DialogContent>
            {renderAnnouncementMessage(previewAnnouncement?.modalMessage || previewAnnouncement?.message)}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AnnouncementHistory;
